import React, { useMemo } from 'react';
import Modal, { DialogHeader, DialogFooter, DialogBody } from './ui/Modal';
import {
  isAppointmentPushType,
  isZoomPushType,
} from '../utils/appointmentHelpers';
import { Bell, Layers } from 'lucide-react';

function formatRelativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const deltaSec = Math.round((Date.now() - then) / 1000);
  if (deltaSec < 45) return 'Just now';
  if (deltaSec < 3600) {
    const m = Math.max(1, Math.floor(deltaSec / 60));
    return `${m}m ago`;
  }
  if (deltaSec < 86400) {
    const h = Math.floor(deltaSec / 3600);
    return `${h}h ago`;
  }
  const d = Math.floor(deltaSec / 86400);
  if (d < 7) return `${d}d ago`;
  return new Date(then).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function formatAbsoluteTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function notificationTypeLabel(typeOrItem) {
  const type =
    typeof typeOrItem === 'string'
      ? typeOrItem
      : typeOrItem?.notificationType || typeOrItem?.type || '';
  const t = String(type || '').toLowerCase();
  const labels = {
    appointment_request: 'Appointment request',
    appointment_accepted: 'Appointment confirmed',
    appointment_declined: 'Appointment declined',
    appointment_reminder: 'Appointment reminder',
    appointment_scheduled: 'Appointment scheduled',
    appointment_cancelled: 'Appointment cancelled',
    appointment_deleted: 'Appointment removed',
    video_waiting_room: 'Video waiting room',
    video_patient_left_call: 'Patient left the call',
    chat_message: 'Message',
    new_alert: 'Alert',
    patient_feeling: 'Patient feeling',
  };
  if (labels[t]) return labels[t];
  if (t.startsWith('zoom_')) return 'Video session';
  if (!t) return 'Notification';
  return t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function notificationOpenLabel(item) {
  const type = String(item?.notificationType || '').toLowerCase();
  if (isZoomPushType(item) || type === 'video_waiting_room' || type === 'video_patient_left_call') {
    return 'Join / open visit';
  }
  if (isAppointmentPushType(item)) return 'Open appointments';
  if (type === 'chat_message' || type === 'message' || type === 'new_message') {
    return 'Open conversation';
  }
  if (type === 'new_alert') return 'Open alerts';
  if (type === 'patient_feeling') return 'Open patients';
  return 'Open';
}

function accentForType(item) {
  const type = String(item?.notificationType || '').toLowerCase();
  if (isZoomPushType(item) || isAppointmentPushType(item)) {
    return { wrap: 'bg-cyan-100', icon: 'text-cyan-700' };
  }
  if (type === 'chat_message' || type === 'message' || type === 'new_message') {
    return { wrap: 'bg-teal-100', icon: 'text-teal-600' };
  }
  if (type === 'new_alert') {
    return { wrap: 'bg-amber-100', icon: 'text-amber-600' };
  }
  if (type === 'patient_feeling') {
    return { wrap: 'bg-sky-100', icon: 'text-sky-600' };
  }
  return { wrap: 'bg-teal-100', icon: 'text-teal-600' };
}

function normalizeSelection(selection) {
  if (!selection) return null;
  if (selection.latest && Array.isArray(selection.items)) return selection;
  return {
    key: selection.id,
    kind: 'single',
    items: [selection],
    latest: selection,
    count: 1,
    unreadCount: selection.read ? 0 : 1,
    read: Boolean(selection.read),
    receivedAt: selection.receivedAt,
    title: selection.title || 'Notification',
    subtitle: selection.body || '',
    typeLabel: notificationTypeLabel(selection),
  };
}

export default function NotificationDetailModal({
  item,
  open,
  onClose,
  onOpen,
}) {
  const stack = useMemo(() => normalizeSelection(item), [item]);
  const latest = stack?.latest;
  const accent = useMemo(() => accentForType(latest), [latest]);
  const openLabel = notificationOpenLabel(latest);
  const absolute = formatAbsoluteTime(stack?.receivedAt);
  const relative = formatRelativeTime(stack?.receivedAt);
  const stacked = (stack?.count || 0) > 1;
  const headerTitle = stacked
    ? stack.title
    : latest?.title || 'Notification';
  const timeSubtitle =
    relative || absolute
      ? `${relative}${absolute ? ` · ${absolute}` : ''}`
      : stack?.typeLabel || '';

  const thread = useMemo(() => {
    if (!stack?.items?.length) return [];
    return [...stack.items].sort(
      (a, b) => new Date(a.receivedAt).getTime() - new Date(b.receivedAt).getTime()
    );
  }, [stack]);

  return (
    <Modal open={open && !!latest} onClose={onClose} labelledBy="notification-detail-title">
      {latest && stack && (
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 w-full max-w-lg max-h-[min(90vh,720px)] overflow-hidden flex flex-col">
          <DialogHeader
            titleId="notification-detail-title"
            title={headerTitle}
            subtitle={timeSubtitle}
            onClose={onClose}
            tone="muted"
          />

          <DialogBody className="px-6 py-5">
            <div className="pb-4 mb-5 border-b border-gray-50 flex items-center gap-3">
              <div
                className={`relative flex-shrink-0 w-10 h-10 rounded-full ${accent.wrap} flex items-center justify-center`}
              >
                {stacked ? (
                  <Layers className={`w-5 h-5 ${accent.icon}`} strokeWidth={2} aria-hidden="true" />
                ) : (
                  <Bell className={`w-5 h-5 ${accent.icon}`} strokeWidth={2} aria-hidden="true" />
                )}
                {stacked ? (
                  <span className="absolute -top-1 -right-1 min-w-[1.25rem] h-5 px-1 rounded-full bg-teal-600 text-white text-[10px] font-bold flex items-center justify-center tabular-nums">
                    {stack.count}
                  </span>
                ) : null}
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wide text-teal-700">
                  {stack.typeLabel}
                </p>
                {stacked ? (
                  <p className="text-xs text-gray-500 mt-0.5">
                    {stack.count} related updates in this thread
                  </p>
                ) : null}
              </div>
            </div>

            {stacked ? (
              <ol className="relative space-y-0 border-l-2 border-teal-100 ml-2">
                {[...thread].reverse().map((entry) => (
                  <li key={entry.id} className="relative pl-5 pb-4 last:pb-0">
                    <span
                      className={`absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full ring-2 ring-white ${
                        entry.read ? 'bg-gray-300' : 'bg-teal-500'
                      }`}
                      aria-hidden="true"
                    />
                    <div className="rounded-xl border border-gray-100 bg-gray-50/70 px-3.5 py-3">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-[11px] font-semibold text-teal-700">
                          {notificationTypeLabel(entry)}
                        </p>
                        <span className="text-[10px] text-gray-400 tabular-nums flex-shrink-0">
                          {formatRelativeTime(entry.receivedAt)}
                        </span>
                      </div>
                      {entry.title && entry.title !== headerTitle ? (
                        <p className="text-sm font-medium text-gray-900 mt-1">{entry.title}</p>
                      ) : null}
                      {entry.body ? (
                        <p className="text-sm text-gray-700 mt-1 leading-relaxed whitespace-pre-wrap">
                          {entry.body}
                        </p>
                      ) : (
                        <p className="text-sm text-gray-500 mt-1 italic">No message body</p>
                      )}
                      <p className="text-[10px] text-gray-400 mt-2">
                        {formatAbsoluteTime(entry.receivedAt)}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            ) : (
              <div className="space-y-5">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                    Details
                  </p>
                  <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
                    {latest.body?.trim()
                      ? latest.body
                      : 'No additional details were included with this notification.'}
                  </p>
                </div>
              </div>
            )}
          </DialogBody>

          <DialogFooter>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex justify-center items-center px-4 py-2.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-white transition-colors"
            >
              Close
            </button>
            <button
              type="button"
              onClick={() => onOpen?.(latest)}
              className="inline-flex justify-center items-center px-4 py-2.5 rounded-lg bg-teal-600 text-sm font-semibold text-white hover:bg-teal-700 transition-colors"
            >
              {openLabel}
            </button>
          </DialogFooter>
        </div>
      )}
    </Modal>
  );
}

export { formatRelativeTime };
